/**
 * 정산 관리 공통 날짜 유틸리티
 * 백엔드 API 응답의 다양한 날짜 형식을 처리하기 위한 유틸리티
 */
import { formatBirthDate } from "@/shared/utils/formatter";

/**
 * 날짜를 YYYY-MM-DD 형식으로 변환 (표시용)
 * @param date 날짜 문자열 또는 배열
 * @returns YYYY-MM-DD 형식의 날짜 문자열
 */
export const formatDateToDisplay = (date: string | any): string => {
    if (!date) return '';

    // 배열인 경우 (백엔드에서 [2025, 10, 8] 형식으로 올 수 있음)
    if (Array.isArray(date)) {
        if (date.length === 3) {
            const [year, month, day] = date.map(String);
            return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
        }
        date = String(date[0]);
    }

    // 문자열로 변환
    date = String(date);

    // 이미 YYYY-MM-DD 형식이면 그대로 반환
    if (date.includes('-') && date.length === 10) return date;

    // YYYY,M,D 또는 YYYY,MM,DD 형식 처리
    if (date.includes(',')) {
        const parts = date.split(',').map((p: string) => p.trim());
        if (parts.length === 3) {
            const [year, month, day] = parts.map(String);
            return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
        }
    }

    // YYYYMMDD 형식은 공통 formatter 사용
    return formatBirthDate(date);
};

/**
 * 날짜를 ISO 형식(YYYY-MM-DD)으로 변환 (API 전송용)
 * @param date YYYYMMDD 또는 YYYY-MM-DD 형식의 날짜 문자열 (또는 배열)
 * @returns YYYY-MM-DD 형식의 날짜 문자열
 */
export const formatDateToISO = (date: string | any): string => {
    if (!date) return '';

    // 배열인 경우 (Axios가 배열로 변환한 경우)
    if (Array.isArray(date)) {
        // [2025, 10, 14] 형식
        if (date.length === 3) {
            const [year, month, day] = date.map(String);
            return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
        }
        // 배열의 첫 번째 요소만 사용
        date = String(date[0]);
    }

    // 문자열로 변환
    date = String(date);

    // 이미 YYYY-MM-DD 형식이면 그대로 반환
    if (date.includes('-') && date.length === 10) return date;

    return formatBirthDate(date);
};

/**
 * 날짜 문자열에서 하이픈 제거 (YYYY-MM-DD -> YYYYMMDD)
 * @param date YYYY-MM-DD 형식의 날짜 문자열
 * @returns YYYYMMDD 형식의 날짜 문자열
 */
export const removeDateHyphens = (date: string): string => {
    return date.replace(/-/g, '');
};


/**
 * 두 날짜 사이의 일수 계산 (시작일, 종료일 포함)
 * @param start YYYY-MM-DD 형식의 시작일
 * @param end YYYY-MM-DD 형식의 종료일
 * @returns 포함 일수 (0 이하면 0 반환)
 */
export function calcDays(start: string, end: string): number {
    if (!start || !end) return 0;
    const diff = Math.round(
        (new Date(end).getTime() - new Date(start).getTime()) / (1000 * 60 * 60 * 24)
    ) + 1;
    return diff > 0 ? diff : 0;
}
